
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import tigermaster from "fdtigermaster-client-sdk";

// 「是否有幫助」回饋區：詢問 → （還是不太清楚時）補充說明 → 感謝，/QA 與 /MobileQA 共用。
// 每次回饋只送出一筆 createFeedback：有用 → 「有用」；不太清楚 → 「沒用」或「沒用｜使用者補充：…」。
@Component
export default class QaFeedback extends Vue {
  // createFeedback 的 type，例如 official_qa_helpful（/QA）、mobile_qa_helpful（/MobileQA）
  @Prop({ required: true }) readonly type!: string;
  // 回饋對應的題目標題
  @Prop({ required: true }) readonly cause!: string;

  step: "ask" | "comment" | "done" = "ask";
  comment = "";

  get textareaId(): string {
    return `qa-feedback-comment-${this.type}`;
  }

  // 同一個元件換題目時回到初始狀態
  @Watch("cause")
  onCauseChange(): void {
    this.step = "ask";
    this.comment = "";
  }

  // 補充說明期間在頁面底部預留鍵盤空間（樣式見 QA.scss 的 body.qa-keyboard-space，只在觸控裝置生效）：
  // 回饋區位在頁面最下方時，頁面已捲到底、無法再往上捲，鍵盤彈出後會被遮住
  @Watch("step")
  onStepChange(step: string): void {
    document.body.classList.toggle("qa-keyboard-space", step === "comment");
  }

  // 鍵盤彈出偵測：按下「還是不太清楚」後的這段時間內監聽 visualViewport resize
  private stopWatchingKeyboard: (() => void) | null = null;

  beforeDestroy(): void {
    this.stopWatchingKeyboard?.();
    window.cancelAnimationFrame(this.scrollFrame);
    document.documentElement.style.scrollBehavior = "";
    document.body.classList.remove("qa-keyboard-space");
  }

  // 按下「還是不太清楚」：切到補充說明並自動聚焦文字框，讓使用者可直接輸入，
  // 並把回饋區捲進視野；手機鍵盤彈出後（可見區域變小）再依剩下的可見區域重新捲動一次
  openComment(): void {
    this.step = "comment";
    this.$nextTick(() => {
      (this.$refs.textarea as HTMLTextAreaElement | undefined)?.focus({ preventScroll: true });
      this.scrollIntoVisibleArea();
      this.watchKeyboard();
    });
  }

  // 把回饋區捲到目前可見區域內：放得下就讓提示、文字框與按鈕全部露出，放不下就讓文字框頂端貼齊可見區域上緣。
  // 用 visualViewport 計算，因為 iOS Safari／新版 Android Chrome 彈出鍵盤時只縮小可見區域、不縮小版面。
  scrollIntoVisibleArea(): void {
    const margin = 16;
    const vv = window.visualViewport;
    const viewTop = vv ? vv.offsetTop : 0;
    const viewHeight = vv ? vv.height : window.innerHeight;
    const block = (this.$el as HTMLElement).getBoundingClientRect();
    const textarea = (this.$refs.textarea as HTMLElement | undefined)?.getBoundingClientRect();

    let delta = 0;
    if (block.height + margin * 2 <= viewHeight) {
      // 整塊放得下：置中於可見區域
      delta = block.top - viewTop - (viewHeight - block.height) / 2;
    } else if (textarea) {
      delta = textarea.top - viewTop - margin;
    }
    if (Math.abs(delta) < 1) return;
    this.scrollByAnimated(delta);
  }

  // 自行控制的捲動動畫：原生 behavior: "smooth" 無法調速度（約 300–500ms，偏慢）
  private scrollFrame = 0;

  scrollByAnimated(delta: number, duration = 200): void {
    window.cancelAnimationFrame(this.scrollFrame);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      window.scrollBy(0, delta);
      return;
    }
    // Bootstrap 在 :root 設了 scroll-behavior: smooth，會讓每一幀的 scrollTo 又被瀏覽器平滑化而拖慢，
    // 動畫期間暫時改回 auto，結束後還原
    const root = document.documentElement;
    root.style.scrollBehavior = "auto";
    const startY = window.scrollY;
    const startTime = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      window.scrollTo(0, startY + delta * easeOutCubic(t));
      if (t < 1) {
        this.scrollFrame = window.requestAnimationFrame(step);
      } else {
        root.style.scrollBehavior = "";
      }
    };
    this.scrollFrame = window.requestAnimationFrame(step);
  }

  watchKeyboard(): void {
    this.stopWatchingKeyboard?.();
    const vv = window.visualViewport;
    if (!vv) return;

    let debounce = 0;
    // 鍵盤展開動畫期間 resize 會連續觸發，等停止變化後再捲動
    const onResize = () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => this.scrollIntoVisibleArea(), 120);
    };
    vv.addEventListener("resize", onResize);
    // 只在按下後短時間內處理，之後使用者自己捲動不受影響
    const expire = window.setTimeout(() => this.stopWatchingKeyboard?.(), 2000);
    this.stopWatchingKeyboard = () => {
      vv.removeEventListener("resize", onResize);
      window.clearTimeout(debounce);
      window.clearTimeout(expire);
      this.stopWatchingKeyboard = null;
    };
  }

  submitComment(): void {
    const text = this.comment.trim();
    this.submit(text ? `沒用｜使用者補充：${text}` : "沒用");
  }

  submit(content: string): void {
    tigermaster.enquire.createFeedback({
      type: this.type,
      cause: this.cause,
      content,
    });
    this.step = "done";
  }
}
